import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { QUERY_KEYS } from 'constants/common';
import { BAROMETER_AREAS, SURVEY_TYPES } from 'constants/surveys';
import { useGetSurveys } from 'hooks/useGetSurveys';
import { SurveyService } from 'services/surveys';
import { type BarometerData, type BarometerResponse } from './types';

export const useBarometerRawData = (): UseQueryResult<BarometerResponse[], Error> => {
  const { data: surveys } = useGetSurveys(SURVEY_TYPES.TEST);
  const barometerSurveyId = surveys?.[surveys.length - 1]?.id;

  return useQuery<AxiosResponse<BarometerResponse[]>, Error, BarometerResponse[]>({
    queryKey: [QUERY_KEYS.FECTH_BAROMETER_HISTORY, barometerSurveyId],
    queryFn: () => SurveyService.getSurveyHistory(barometerSurveyId ?? ''),
    select: response => response.data.filter(result => result.completed),
    enabled: Boolean(barometerSurveyId),
  });
};

export const useFetchBarometerResults = (): { data: BarometerData; isLoading: boolean } => {
  const { data: historyData, isLoading, isFetched } = useBarometerRawData();

  const latestResult = historyData?.sort(
    (first, second) =>
      new Date(second.completedAt ?? 0).getTime() - new Date(first.completedAt ?? 0).getTime()
  )[0];

  const scorePerTag = latestResult?.scorePerTag ?? {};
  const maxScorePerTag = latestResult?.maxScorePerTag ?? {};

  return {
    isLoading: isLoading || !isFetched,
    data: {
      [BAROMETER_AREAS.GENERAL]: {
        score: latestResult?.totalSurveyScore ?? 0,
        maxScore: latestResult?.maxSurveyScore ?? 100,
      },
      [BAROMETER_AREAS.EMOTIONAL]: {
        score: scorePerTag[BAROMETER_AREAS.EMOTIONAL] ?? 0,
        maxScore: maxScorePerTag[BAROMETER_AREAS.EMOTIONAL] ?? 100,
      },
      [BAROMETER_AREAS.RELATIONAL]: {
        score: scorePerTag[BAROMETER_AREAS.RELATIONAL] ?? 0,
        maxScore: maxScorePerTag[BAROMETER_AREAS.RELATIONAL] ?? 100,
      },
      [BAROMETER_AREAS.PROFESSIONAL]: {
        score: scorePerTag[BAROMETER_AREAS.PROFESSIONAL] ?? 0,
        maxScore: maxScorePerTag[BAROMETER_AREAS.PROFESSIONAL] ?? 100,
      },
      [BAROMETER_AREAS.HEALTH]: {
        score: scorePerTag[BAROMETER_AREAS.HEALTH] ?? 0,
        maxScore: maxScorePerTag[BAROMETER_AREAS.HEALTH] ?? 100,
      },
    },
  };
};
